import { useState } from 'react';
import { ContentDocumentView } from '../components/content-document';
import { ErrorPage, LoadingPage } from '../components/resource-state';
import { loadWeek, type Manifest, type Week } from '../content/data';
import { useResource } from '../content/use-resource';
import { completedDayCount, firstIncompleteDayId, isDayUnlocked } from '../progress/progress';
import { useProgress } from '../progress/progress-context';

function weekCompletion(manifest: Manifest, weekId: string, completedDayIds: string[]): number {
  const completed = new Set(completedDayIds);
  const week = manifest.weeks.find((candidate) => candidate.id === weekId);
  if (!week) return 0;
  return Array.from({ length: 7 }, (_, index) => `${week.id}-day-${String(index + 1).padStart(2, '0')}`)
    .filter((dayId) => completed.has(dayId)).length;
}

type RoadmapMode = 'list' | 'chart';
type RoadmapStatus = 'Complete' | 'Current' | 'Started' | 'Later';
type ManifestWeek = Manifest['weeks'][number];

function roadmapStatus(done: number, current: boolean): RoadmapStatus {
  if (done === 7) return 'Complete';
  if (current) return 'Current';
  if (done > 0) return 'Started';
  return 'Later';
}

function currentDayNumber(currentDayId: string | null, weekId: string): number {
  if (!currentDayId?.startsWith(`${weekId}-day-`)) return 1;
  const parsed = Number(currentDayId.slice(-2));
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 7 ? parsed : 1;
}

function RoadmapList({
  manifest,
  selectedWeekId,
  currentDayId,
  completedDayIds,
  onSelectWeek
}: {
  manifest: Manifest;
  selectedWeekId: string;
  currentDayId: string | null;
  completedDayIds: string[];
  onSelectWeek: (week: ManifestWeek) => void;
}) {
  return (
    <ol className="roadmap-list" aria-label={`${manifest.totals.weeks}-week list roadmap`}>
      {manifest.weeks.map((week) => {
        const done = weekCompletion(manifest, week.id, completedDayIds);
        const current = currentDayId?.startsWith(week.id) ?? false;
        const status = roadmapStatus(done, current);
        const selected = selectedWeekId === week.id;

        return (
          <li className={`${current ? 'is-current' : ''} ${selected ? 'is-selected' : ''}`} key={week.id}>
            <button
              className="roadmap-week-button"
              type="button"
              aria-label={`View Week ${week.number}: ${week.title}`}
              aria-pressed={selected}
              aria-controls="roadmap-week-preview"
              onClick={() => onSelectWeek(week)}
            >
              <span className="roadmap-number">{String(week.number).padStart(2, '0')}</span>
              <span className="roadmap-main">
                <span className="roadmap-title-row">
                  <span>
                    <strong>{week.title}</strong>
                    <small>{week.mainProject}: {week.milestone.replace(/^Milestone \d+:\s*/i, '')}</small>
                  </span>
                  <span className={`status-label status-label--${status.toLowerCase()}`}>{status}</span>
                </span>
                <span className="week-progress" aria-label={`${done} of 7 days complete`} role="progressbar" aria-valuemin={0} aria-valuemax={7} aria-valuenow={done}>
                  {Array.from({ length: 7 }, (_, index) => <span className={index < done ? 'is-done' : ''} key={index} />)}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function RoadmapChart({
  manifest,
  selectedWeekId,
  currentDayId,
  completedDayIds,
  onSelectWeek
}: {
  manifest: Manifest;
  selectedWeekId: string;
  currentDayId: string | null;
  completedDayIds: string[];
  onSelectWeek: (week: ManifestWeek) => void;
}) {
  return (
    <ol className="roadmap-chart" aria-label={`${manifest.totals.weeks}-week chart roadmap`}>
      {manifest.weeks.map((week) => {
        const done = weekCompletion(manifest, week.id, completedDayIds);
        const current = currentDayId?.startsWith(week.id) ?? false;
        const status = roadmapStatus(done, current);
        const selected = selectedWeekId === week.id;

        return (
          <li className={`roadmap-chart-item ${current ? 'is-current' : ''} ${selected ? 'is-selected' : ''}`} key={week.id}>
            <button
              className="roadmap-chart-node"
              type="button"
              aria-label={`View Week ${week.number}: ${week.title}`}
              aria-pressed={selected}
              aria-controls="roadmap-week-preview"
              onClick={() => onSelectWeek(week)}
            >
              <span className="roadmap-chart-kicker">Week {String(week.number).padStart(2, '0')}</span>
              <strong>{week.title}</strong>
              <small>{week.mainProject}</small>
              <span className="roadmap-chart-footer">
                <span>{done} / 7 days</span>
                <span className={`status-label status-label--${status.toLowerCase()}`}>{status}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

function RoadmapWeekPreview({ manifest, weekId, currentDayId }: { manifest: Manifest; weekId: string; currentDayId: string | null }) {
  const resource = useResource(() => loadWeek(manifest, weekId), [weekId]);
  const progress = useProgress();
  const initialDay = currentDayNumber(currentDayId, weekId);
  const [selectedDayNumber, setSelectedDayNumber] = useState(initialDay);

  if (resource.status === 'loading') {
    return (
      <section className="roadmap-preview roadmap-preview--state" id="roadmap-week-preview" aria-label="Week day preview" aria-live="polite">
        <p>Loading the week preview…</p>
      </section>
    );
  }

  if (resource.status === 'error') {
    return (
      <section className="roadmap-preview roadmap-preview--state" id="roadmap-week-preview" aria-label="Week day preview">
        <h2>The preview could not load</h2>
        <p>{resource.error.message}</p>
        <button className="secondary-button" type="button" onClick={resource.retry}>Try again</button>
      </section>
    );
  }

  const week = resource.data;
  const selectedDay = week.days.find((day) => day.number === selectedDayNumber) ?? week.days[0];
  const selectedRow = week.dailyMap.find((row) => row.dayNumber === selectedDayNumber) ?? week.dailyMap[0];

  if (!selectedDay || !selectedRow) return null;

  const dayId = selectedDay.id;
  const completed = progress.completedDayIds.includes(dayId);
  const current = currentDayId === dayId;
  const unlocked = isDayUnlocked(manifest, progress, dayId);
  const dayStatus = completed ? 'Complete' : current ? 'Current' : 'Preview';

  return (
    <section
      className="roadmap-preview"
      id="roadmap-week-preview"
      aria-label={`Week ${week.number} day preview`}
    >
      <header className="roadmap-preview-heading">
        <div>
          <span>Week {week.number}</span>
          <h2>{week.title}</h2>
        </div>
        <a href={`#/week/${week.id}`}>Week overview</a>
      </header>

      <p className="roadmap-preview-intro">Choose any day to inspect its plan. Future days remain read-only until they become current.</p>

      <ol className="roadmap-day-picker" aria-label={`Days in Week ${week.number}`}>
        {week.days.map((day) => {
          const isSelected = day.number === selectedDay.number;
          const isComplete = progress.completedDayIds.includes(day.id);
          const isCurrent = currentDayId === day.id;
          const label = isComplete ? 'Done' : isCurrent ? 'Now' : 'View';
          return (
            <li key={day.id}>
              <button
                type="button"
                aria-label={`${day.weekday}, Day ${day.number}: ${day.title}`}
                aria-pressed={isSelected}
                onClick={() => setSelectedDayNumber(day.number)}
              >
                <span>{day.weekday.slice(0, 3)}</span>
                <strong>{day.number}</strong>
                <small>{label}</small>
              </button>
            </li>
          );
        })}
      </ol>

      <article className="roadmap-day-card" aria-live="polite">
        <header>
          <div>
            <span>{selectedDay.weekday} · Day {selectedDay.number}</span>
            <h3>{selectedDay.title}</h3>
          </div>
          <span className={`roadmap-day-status roadmap-day-status--${dayStatus.toLowerCase()}`}>{dayStatus}</span>
        </header>

        <dl className="roadmap-day-plan">
          <div>
            <dt>Learning focus</dt>
            <dd>{selectedRow.learning}</dd>
          </div>
          <div>
            <dt>Mini project</dt>
            <dd>{selectedRow.miniProject}</dd>
          </div>
          <div>
            <dt>Main project slice</dt>
            <dd>{selectedRow.mainProjectSlice}</dd>
          </div>
          <div>
            <dt>Interview gate</dt>
            <dd>{selectedDay.questionIds.length > 0 ? selectedDay.questionIds.join(', ') : 'No assigned questions'}</dd>
          </div>
        </dl>

        <footer className="roadmap-preview-actions">
          <a className="secondary-button" href={`#/project/${week.id}`}>Project requirements</a>
          {unlocked ? (
            <a className="primary-button" href={`#/day/${dayId}`}>{current ? 'Continue current day' : 'Review completed day'}</a>
          ) : (
            <p><strong>Preview only.</strong> Finish the current day before doing this work.</p>
          )}
        </footer>
      </article>
    </section>
  );
}

export function RoadmapPage({ manifest }: { manifest: Manifest }) {
  const progress = useProgress();
  const currentDayId = firstIncompleteDayId(manifest, progress);
  const currentWeekId = manifest.weeks.find((week) => currentDayId?.startsWith(week.id))?.id;
  const [selectedWeekId, setSelectedWeekId] = useState(currentWeekId ?? manifest.weeks[0]?.id ?? 'week-01');
  const [mode, setMode] = useState<RoadmapMode>('list');

  function selectWeek(week: ManifestWeek) {
    setSelectedWeekId(week.id);
    if (typeof window.matchMedia !== 'function' || !window.matchMedia('(max-width: 980px)').matches) return;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.getElementById('roadmap-week-preview')?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
      });
    });
  }

  return (
    <main className="wide-page" id="main-content">
      <header className="page-heading roadmap-heading">
        <div>
          <h1>The sequence</h1>
          <p>Choose any week, then preview all seven days. Completion still moves one day at a time.</p>
        </div>
        <p className="roadmap-count"><strong>{completedDayCount(manifest, progress)}</strong> of {manifest.totals.days} days closed</p>
      </header>

      <div className="roadmap-toolbar">
        <div>
          <strong>Roadmap layout</strong>
          <span>Switch views without losing your selected week.</span>
        </div>
        <div className="roadmap-view-toggle" role="group" aria-label="Roadmap layout">
          <button type="button" aria-pressed={mode === 'list'} onClick={() => setMode('list')}>List</button>
          <button type="button" aria-pressed={mode === 'chart'} onClick={() => setMode('chart')}>Chart</button>
        </div>
      </div>

      <div className="roadmap-explorer-layout">
        <div className="roadmap-view" data-view={mode}>
          {mode === 'list' ? (
            <RoadmapList
              manifest={manifest}
              selectedWeekId={selectedWeekId}
              currentDayId={currentDayId}
              completedDayIds={progress.completedDayIds}
              onSelectWeek={selectWeek}
            />
          ) : (
            <RoadmapChart
              manifest={manifest}
              selectedWeekId={selectedWeekId}
              currentDayId={currentDayId}
              completedDayIds={progress.completedDayIds}
              onSelectWeek={selectWeek}
            />
          )}
        </div>

        <RoadmapWeekPreview key={selectedWeekId} manifest={manifest} weekId={selectedWeekId} currentDayId={currentDayId} />
      </div>
    </main>
  );
}

type ProjectGroup = {
  name: string;
  weeks: Manifest['weeks'];
};

function groupProjects(manifest: Manifest): ProjectGroup[] {
  const groups = new Map<string, Manifest['weeks']>();
  for (const week of manifest.weeks) {
    const existing = groups.get(week.mainProject);
    if (existing) existing.push(week);
    else groups.set(week.mainProject, [week]);
  }
  return [...groups].map(([name, weeks]) => ({ name, weeks }));
}

export function ProjectsPage({ manifest }: { manifest: Manifest }) {
  const groups = groupProjects(manifest);
  return (
    <main className="wide-page" id="main-content">
      <header className="page-heading">
        <h1>Main projects</h1>
        <p>Each row is one project. Its milestones appear in the order you build them.</p>
      </header>

      <div className="project-ledger">
        {groups.map((group, projectIndex) => (
          <section className="project-entry" key={group.name}>
            <header>
              <span>Project {projectIndex + 1}</span>
              <h2>{group.name}</h2>
              <p>{group.weeks.length === 1 ? 'One weekly milestone' : `${group.weeks.length} weekly milestones`}</p>
            </header>
            <ol>
              {group.weeks.map((week) => (
                <li key={week.id}>
                  <div>
                    <span>Week {week.number}</span>
                    <strong>{week.milestone}</strong>
                    <small>{week.title}</small>
                  </div>
                  <a href={`#/project/${week.id}`}>Requirements</a>
                </li>
              ))}
            </ol>
          </section>
        ))}
      </div>
    </main>
  );
}

function WeekOverview({ manifest, week }: { manifest: Manifest; week: Week }) {
  const progress = useProgress();
  const currentDayId = firstIncompleteDayId(manifest, progress);

  return (
    <main className="reading-page" id="main-content">
      <a className="back-link" href="#/roadmap">Back to the sequence</a>
      <header className="page-heading week-heading">
        <p>Week {week.number}</p>
        <h1>{week.title}</h1>
        <dl>
          <div><dt>Main project</dt><dd>{week.mainProject}</dd></div>
          <div><dt>Milestone</dt><dd>{week.milestone}</dd></div>
        </dl>
      </header>

      <section className="outcome-strip">
        <h2>Outcome</h2>
        <p>{week.outcome}</p>
      </section>

      <ContentDocumentView document={week.overview} manifest={manifest} excludeHeadings={['Daily map', 'Main project', 'Next']} />

      <section className="daily-map" aria-labelledby="daily-map-heading">
        <div className="section-heading-row">
          <div>
            <h2 id="daily-map-heading">Daily map</h2>
            <p>The build slice stays small enough to finish inside the day.</p>
          </div>
          <a className="secondary-button" href={`#/project/${week.id}`}>Project requirements</a>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr><th>Day</th><th>Learn</th><th>Mini project</th><th>Main project slice</th><th><span className="sr-only">Open day</span></th></tr>
            </thead>
            <tbody>
              {week.dailyMap.map((row) => {
                const dayId = `${week.id}-day-${String(row.dayNumber).padStart(2, '0')}`;
                const unlocked = isDayUnlocked(manifest, progress, dayId);
                const current = currentDayId === dayId;
                return (
                  <tr className={current ? 'is-current' : ''} key={dayId}>
                    <th scope="row">{row.weekday}</th>
                    <td>{row.learning}</td>
                    <td>{row.miniProject}</td>
                    <td>{row.mainProjectSlice}</td>
                    <td>{unlocked ? <a href={`#/day/${dayId}`}>{current ? 'Start' : 'Review'}</a> : <span>Later</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function ProjectRequirements({ manifest, week }: { manifest: Manifest; week: Week }) {
  return (
    <main className="reading-page" id="main-content">
      <a className="back-link" href="#/projects">Back to main projects</a>
      <header className="page-heading project-heading">
        <p>{week.mainProject}, Week {week.number}</p>
        <h1>{week.milestone}</h1>
        <span>{week.projectScope.length} scoped requirements</span>
      </header>

      <section className="scope-index" aria-labelledby="scope-heading">
        <h2 id="scope-heading">Scope index</h2>
        <ol>
          {week.projectScope.map((item) => (
            <li key={item.id}><code>{item.id}</code><span>{item.text}</span></li>
          ))}
        </ol>
      </section>

      <ContentDocumentView document={week.project} manifest={manifest} />
    </main>
  );
}

function WeekResourcePage({ manifest, weekId, mode }: { manifest: Manifest; weekId: string; mode: 'week' | 'project' }) {
  const resource = useResource(() => loadWeek(manifest, weekId), [weekId]);
  if (resource.status === 'loading') return <LoadingPage label={mode === 'week' ? 'Opening the week overview' : 'Opening project requirements'} />;
  if (resource.status === 'error') return <ErrorPage error={resource.error} retry={resource.retry} />;
  return mode === 'week'
    ? <WeekOverview manifest={manifest} week={resource.data} />
    : <ProjectRequirements manifest={manifest} week={resource.data} />;
}

export function WeekPage({ manifest, weekId }: { manifest: Manifest; weekId: string }) {
  return <WeekResourcePage manifest={manifest} weekId={weekId} mode="week" />;
}

export function ProjectPage({ manifest, weekId }: { manifest: Manifest; weekId: string }) {
  return <WeekResourcePage manifest={manifest} weekId={weekId} mode="project" />;
}
